import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import MDBox from "components/MDBox";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  Icon,
  IconButton,
  Typography,
} from "@mui/material";

// Custom Components
import LoanDataTable from "dataTables/submittedLoan/submitterLoan";
import DataTable from "examples/Tables/DataTable";
import BasicModal from "examples/modal/BasicModal/BasicModal";
import ConfigDropdown from "components/Dropdown/ConfigDropdown";
import FDropdown2 from "components/Dropdown/fDropdown2";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import ResetFilterButton from "components/Buttons/ResetButton";
import CustomSearch from "components/Search/Search";
import UpdateLeadBay from "examples/modal/UpdateLeadBay";
import FilterDropdown from "components/Dropdown/FilterDropdown";
import ConfirmationModal from "examples/modal/Confirmation/Confirmation";

// Sessions
import Session from "utils/Sessions";

// Constant
import Constants, { defaultData, ModalContent, ButtonTitles, Icons, Colors } from "utils/Constants";

// Redux component
import { openSnackbar } from "redux/Slice/Notification";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllSubmitterLoanFormThunk,
  updateLeadDataThunk,
  exportLoanThunk,
  deleteLeadThunk,
} from "redux/Thunks/LoanFormConfig";
import getAllBanks, { getAllCodes, getAllSalesManagers } from "redux/Thunks/leadUtilsThunks";
import FTextField from "components/Form/FTextField";
import pxToRem from "assets/theme/functions/pxToRem";
import { paramCreater } from "utils/methods/methods";
import ViewCibil from "examples/modal/Cibil/ViewCibil";
import MDTypography from "components/MDTypography";
import { removeLoanData, updateLoanData } from "redux/Slice/LoanConfig.slice";
import uploadImageThunk from "redux/Thunks/ImageUpload";
import FullScreenImageComponent from "components/ViewFullImage/ViewImage";
import Pdf from "assets/images/pdf.svg";

const renderBankOption = (props, option) =>
  React.createElement(
    "li",
    props,
    <MDBox display="flex" alignItems="center" gap={1} width="100%">
      {option?.image && (
        <MDBox
          component="img"
          src={option.image}
          alt={option.title || "Bank"}
          sx={{ width: 42, height: 24, objectFit: "contain", flexShrink: 0 }}
        />
      )}
      <Typography variant="body2" sx={{ fontSize: pxToRem(14), color: "#344054" }}>
        {option?.title || ""}
      </Typography>
    </MDBox>
  );

const formatDateString = (dateVal, fallbackVal = "") => {
  const chosenDate = dateVal || fallbackVal;
  if (!chosenDate) return "";
  return String(chosenDate).split("T")[0];
};

const getFYOptions = () => {
  const now = new Date();
  const currentFYStart = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
  const list = [];
  for (let y = currentFYStart + 1; y >= currentFYStart - 3; y -= 1) {
    list.push(`${y}-${String(y + 1).slice(-2)}`);
  }
  if (!list.includes("2026-27")) list.push("2026-27");
  if (!list.includes("2025-26")) list.push("2025-26");
  return list;
};

const convertDashboardMonthToCode = (val) => {
  if (!val || val === "all") return "all";
  const numToCode = {
    1: "jan",
    2: "feb",
    3: "mar",
    4: "apr",
    5: "may",
    6: "jun",
    7: "jul",
    8: "aug",
    9: "sep",
    10: "oct",
    11: "nov",
    12: "dec",
  };
  return numToCode[val] || numToCode[String(val)] || String(val).toLowerCase();
};

const mapLoanTypeToDropdownTitle = (typeInput) => {
  if (!typeInput || typeInput === "all") return "all";
  const normalized = String(typeInput).toLowerCase().trim();
  if (normalized.includes("property")) return "Loan Against Property";
  if (normalized.includes("security")) return "Loan Against Security";
  if (normalized.includes("capital")) return "Working Capital";
  if (normalized.includes("machinery")) return "Machinery Loan";
  if (normalized.includes("gold")) return "Gold Loan";
  if (normalized.includes("education")) return "Education Loan";
  if (normalized.includes("card") || normalized === "credit") return "Credit Card";
  if (normalized.includes("car")) return "Car Loan";
  if (normalized.includes("business")) return "Business Loan";
  if (normalized.includes("home")) return "Home Loan";
  if (normalized.includes("personal")) return "Personal Loan";
  return typeInput;
};

const mapLoanTypeToBackend = (typeTitle) => {
  if (!typeTitle || typeTitle === "all") return "";
  const normalized = String(typeTitle).toLowerCase().trim();
  if (normalized.includes("property")) return "loanAgainstProperty";
  if (normalized.includes("security")) return "loanAgainstSecurity";
  if (normalized.includes("capital")) return "workingCapital";
  if (normalized.includes("machinery")) return "machinery";
  if (normalized.includes("gold")) return "gold";
  if (normalized.includes("education")) return "education";
  if (normalized.includes("card") || normalized === "credit") return "credit";
  if (normalized.includes("car")) return "car";
  if (normalized.includes("business")) return "business";
  if (normalized.includes("home")) return "home";
  if (normalized.includes("personal")) return "personal";
  return typeTitle.toLowerCase().split(" ")[0];
};

const isCreditCardLead = (type) => {
  const t = String(type || "")
    .toLowerCase()
    .trim();
  return t.includes("card") || t === "credit";
};

const initialEditModal = {
  open: false,
  id: "",
  status: "open",
  sourceOfLead: "",
  imd: "",
  refBy: "",
  fileNumber: "",
  profileType: "",
  salesManager: "",
  bank: "",
  code: "",
  currentStatus: "",
  leadDate: "",
  loginDate: "",
  dateOfDisbursment: "",
  cardDeliveryDate: "",
  remark: "",
};

const initialMailState = { openModal: false, sendMail: false };

const initialFetchCibil = {
  loanFormId: "",
  name: "",
  mobile: "",
  gender: "",
  pan: "",
  open: false,
};

const DEFAULT_VISIBLE_COLUMNS = [
  "srNo",
  "leadDate",
  "loginDate",
  "type",
  "name",
  "phone",
  "loanAmount",
  "currentStatus",
  "cardDeliveryDate",
  "imd",
  "bank",
  "action",
];

const LEAD_BAY_FILTER_VISIBILITY_KEY = "leadBayVisibleFilters";
const DEFAULT_VISIBLE_FILTER_IDS = [
  "type",
  "currentStatus",
  "status",
  "financialYear",
  "month",
  "bank",
  "code",
  "salesManager",
  "firstName",
];

const getSavedVisibleFilterIds = () => {
  try {
    const savedFilters = JSON.parse(localStorage.getItem(LEAD_BAY_FILTER_VISIBILITY_KEY));
    return Array.isArray(savedFilters) ? savedFilters : null;
  } catch (error) {
    return null;
  }
};

const applyFilterVisibility = (options, visibleFilterIds) =>
  options.map((option) => ({
    ...option,
    checked: visibleFilterIds ? visibleFilterIds.includes(option.id) : option.checked,
  }));

function feedbacks({ leadActions } = {}) {
  const location = useLocation();
  const isFirstMount = useRef(true);

  const initialCurrentStatus = location.state?.currentStatus || "all";
  const initialFinancialYear = location.state?.financialYear || "all";
  const initialMonth = convertDashboardMonthToCode(location.state?.month);
  const initialType = mapLoanTypeToDropdownTitle(location.state?.type);

  const savedVisibleFilterIds = useMemo(() => getSavedVisibleFilterIds(), []);
  const leadSources = ["Direct Offline", "Direct Online", "IMD Channel"];
  const profileTypeOptions = [
    { [Constants.MONGOOSE_ID]: "self_employed", title: "Self Employed" },
    { [Constants.MONGOOSE_ID]: "salaried", title: "Salaried" },
  ];
  const loanStatuses = [
    { [Constants.MONGOOSE_ID]: "Document Verification", title: "Document Verification" },
    { [Constants.MONGOOSE_ID]: "Application Submitted", title: "Application Submitted" },
    { [Constants.MONGOOSE_ID]: "Pending with Credit", title: "Pending with Credit" },
    { [Constants.MONGOOSE_ID]: "Offer Received", title: "Offer Received" },
    { [Constants.MONGOOSE_ID]: "Offer Selected", title: "Offer Selected" },
    { [Constants.MONGOOSE_ID]: "Loan Sanctioned", title: "Loan Sanctioned" },
    { [Constants.MONGOOSE_ID]: "Disbursement Documentation", title: "Disbursement Documentation" },
    { [Constants.MONGOOSE_ID]: "Disbursement", title: "Disbursement" },
    {
      [Constants.MONGOOSE_ID]: "Amount Credited/Cheque issued",
      title: "Amount Credited/Cheque issued",
    },
    { [Constants.MONGOOSE_ID]: "Rejected By Bank", title: "Rejected By Bank" },
    { [Constants.MONGOOSE_ID]: "Denied By Customer", title: "Denied By Customer" },
  ];

  const creditCardStatuses = [
    { [Constants.MONGOOSE_ID]: "Document Verification", title: "Document Verification" },
    { [Constants.MONGOOSE_ID]: "Application Submitted", title: "Application Submitted" },
    { [Constants.MONGOOSE_ID]: "Pending with Credit", title: "Pending with Credit" },
    { [Constants.MONGOOSE_ID]: "Card Approved", title: "Card Approved" },
    { [Constants.MONGOOSE_ID]: "Card Delivered", title: "Card Delivered" },
    { [Constants.MONGOOSE_ID]: "Rejected By Bank", title: "Rejected By Bank" },
    { [Constants.MONGOOSE_ID]: "Denied By Customer", title: "Denied By Customer" },
  ];

  const currentStatus = [
    { [Constants.MONGOOSE_ID]: "Document Verification", title: "Document Verification" },
    { [Constants.MONGOOSE_ID]: "Application Submitted", title: "Application Submitted" },
    { [Constants.MONGOOSE_ID]: "Pending with Credit", title: "Pending with Credit" },
    { [Constants.MONGOOSE_ID]: "Offer Received", title: "Offer Received" },
    { [Constants.MONGOOSE_ID]: "Offer Selected", title: "Offer Selected" },
    { [Constants.MONGOOSE_ID]: "Loan Sanctioned", title: "Loan Sanctioned" },
    { [Constants.MONGOOSE_ID]: "Disbursement Documentation", title: "Disbursement Documentation" },
    { [Constants.MONGOOSE_ID]: "Disbursement", title: "Disbursement" },
    {
      [Constants.MONGOOSE_ID]: "Amount Credited/Cheque issued",
      title: "Amount Credited/Cheque issued",
    },
    { [Constants.MONGOOSE_ID]: "Card Approved", title: "Card Approved" },
    { [Constants.MONGOOSE_ID]: "Card Delivered", title: "Card Delivered" },
    { [Constants.MONGOOSE_ID]: "Rejected By Bank", title: "Rejected By Bank" },
    { [Constants.MONGOOSE_ID]: "Denied By Customer", title: "Denied By Customer" },
  ];

  const [search, setSearch] = useState(() =>
    applyFilterVisibility(
      [
        {
          inputLabel: "Search By Name",
          list: [],
          selectedValue: "",
          isLoading: false,
          title: "Name",
          checked: true,
          id: "firstName",
          index: 0,
        },
        {
          inputLabel: "Search By Mobile Number",
          list: [],
          selectedValue: "",
          isLoading: false,
          title: "Mobile Number",
          checked: false,
          id: "mobileNumber",
          index: 1,
        },
        {
          inputLabel: "Search By IMD",
          list: [],
          selectedValue: "",
          isLoading: false,
          title: "IMD",
          checked: false,
          id: "imd",
          index: 2,
        },
      ],
      savedVisibleFilterIds
    )
  );
  const [filters, setFilters] = useState(() => {
    const rawFilters = [
      {
        inputLabel: "Product Type",
        id: "type",
        checked: true,
        list: [
          { [Constants.MONGOOSE_ID]: "all", title: "All" },
          { [Constants.MONGOOSE_ID]: "Personal Loan", title: "Personal Loan" },
          { [Constants.MONGOOSE_ID]: "Home Loan", title: "Home Loan" },
          { [Constants.MONGOOSE_ID]: "Business Loan", title: "Business Loan" },
          { [Constants.MONGOOSE_ID]: "Car Loan", title: "Car Loan" },
          { [Constants.MONGOOSE_ID]: "Credit Card", title: "Credit Card" },
          { [Constants.MONGOOSE_ID]: "Education Loan", title: "Education Loan" },
          { [Constants.MONGOOSE_ID]: "Loan Against Property", title: "Loan Against Property" },
          { [Constants.MONGOOSE_ID]: "Loan Against Security", title: "Loan Against Security" },
          { [Constants.MONGOOSE_ID]: "Working Capital", title: "Working Capital" },
          { [Constants.MONGOOSE_ID]: "Machinery Loan", title: "Machinery Loan" },
          { [Constants.MONGOOSE_ID]: "Gold Loan", title: "Gold Loan" },
        ],
        selectedValue: initialType,
      },
      {
        inputLabel: "Current Status",
        id: "currentStatus",
        checked: true,
        list: [{ [Constants.MONGOOSE_ID]: "all", title: "All" }, ...currentStatus],
        selectedValue: initialCurrentStatus,
      },
      {
        inputLabel: "Status",
        id: "status",
        checked: true,
        list: [
          { [Constants.MONGOOSE_ID]: "all", title: "All" },
          { [Constants.MONGOOSE_ID]: "open", title: "Open" },
          { [Constants.MONGOOSE_ID]: "close", title: "Close" },
          { [Constants.MONGOOSE_ID]: "hold", title: "Hold" },
          { [Constants.MONGOOSE_ID]: "completed", title: "Completed" },
        ],
        selectedValue: "all",
      },
      {
        inputLabel: "Financial Year",
        id: "financialYear",
        checked: true,
        list: [
          { [Constants.MONGOOSE_ID]: "all", title: "All" },
          ...getFYOptions().map((fy) => ({
            [Constants.MONGOOSE_ID]: fy,
            title: `FY ${fy}`,
          })),
        ],
        selectedValue: initialFinancialYear,
      },
      {
        inputLabel: "Month",
        id: "month",
        checked: true,
        list: [
          { [Constants.MONGOOSE_ID]: "all", title: "All" },
          { [Constants.MONGOOSE_ID]: "apr", title: "Apr" },
          { [Constants.MONGOOSE_ID]: "may", title: "May" },
          { [Constants.MONGOOSE_ID]: "jun", title: "Jun" },
          { [Constants.MONGOOSE_ID]: "jul", title: "Jul" },
          { [Constants.MONGOOSE_ID]: "aug", title: "Aug" },
          { [Constants.MONGOOSE_ID]: "sep", title: "Sep" },
          { [Constants.MONGOOSE_ID]: "oct", title: "Oct" },
          { [Constants.MONGOOSE_ID]: "nov", title: "Nov" },
          { [Constants.MONGOOSE_ID]: "dec", title: "Dec" },
          { [Constants.MONGOOSE_ID]: "jan", title: "Jan" },
          { [Constants.MONGOOSE_ID]: "feb", title: "Feb" },
          { [Constants.MONGOOSE_ID]: "mar", title: "Mar" },
        ],
        selectedValue: initialMonth,
      },
      {
        inputLabel: "Bank",
        id: "bank",
        checked: true,
        list: [{ [Constants.MONGOOSE_ID]: "all", title: "All" }],
        selectedValue: "all",
      },
      {
        inputLabel: "Code",
        id: "code",
        checked: true,
        list: [{ [Constants.MONGOOSE_ID]: "all", title: "All" }],
        selectedValue: "all",
      },
      {
        inputLabel: "Sales Manager",
        id: "salesManager",
        checked: true,
        list: [{ [Constants.MONGOOSE_ID]: "all", title: "All" }],
        selectedValue: "all",
      },
    ];

    const withVisibility = applyFilterVisibility(rawFilters, savedVisibleFilterIds);
    return withVisibility.map((f) => {
      if (
        (f.id === "currentStatus" && initialCurrentStatus !== "all") ||
        (f.id === "financialYear" && initialFinancialYear !== "all") ||
        (f.id === "month" && initialMonth !== "all") ||
        (f.id === "type" && initialType !== "all")
      ) {
        return { ...f, checked: true };
      }
      return f;
    });
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editModal, setEditModal] = useState(initialEditModal);
  const [screenIndex, setScreenIndex] = useState(0);
  const [openEdit, setOpenEdit] = useState(false);
  const [updateLeadbay, setUpdateLeadBay] = useState({});
  const [leadyBayId, setLeadyBayId] = useState("");
  const [error, setError] = useState({});
  const [mailModal, setMailModal] = useState(initialMailState);
  const [salesManagerOptions, setSalesManagerOptions] = useState([]);
  const [tablePagination, setTablePagination] = useState({
    page: 0,
    perPage: defaultData.DATE_ON_SINGLE_API_CALL,
  });
  const [next, setNext] = useState(0);
  const [fetchCibil, setFetchCibil] = useState(JSON.parse(JSON.stringify(initialFetchCibil)));
  const [deleteData, setDeleteData] = useState({ open: false, id: "", loading: false });

  const editStatusMenu = useMemo(() => {
    const isCredit = isCreditCardLead(editModal.data?.type);
    const baseOptions = isCredit ? creditCardStatuses : loanStatuses;
    if (
      editModal.currentStatus &&
      !baseOptions.some((opt) => opt[Constants.MONGOOSE_ID] === editModal.currentStatus)
    ) {
      return [
        ...baseOptions,
        {
          [Constants.MONGOOSE_ID]: editModal.currentStatus,
          title: editModal.currentStatus,
        },
      ];
    }
    return baseOptions;
  }, [editModal.data?.type, editModal.currentStatus]);

  const loanData = useSelector((state) => state.loan);

  let debounceTimeout;

  const [bankList, setBankList] = useState([]);
  const [codeList, setCodeList] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [managePanel, setManagePanel] = useState({ open: false, type: "columns" });
  const [tempVisibleColumns, setTempVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [tempFilterOptions, setTempFilterOptions] = useState([]);
  const dispatch = useDispatch();

  const handleEditModal = (data) => {
    setEditModal({
      open: true,
      data,
      id: data?.[Constants.MONGOOSE_ID],
      status: data?.status,
      sourceOfLead: data?.sourceOfLead,
      imd: data?.imd,
      refBy: data?.refBy,
      fileNumber: data?.fileNumber || "",
      profileType: data?.profileType || "",
      salesManager: data?.salesManager?.sm || data?.salesManager || "",
      bank: data?.bank?.[Constants.MONGOOSE_ID],
      code: data?.code?.[Constants.MONGOOSE_ID],
      currentStatus: data?.currentStatus,
      remark: data?.remark,
      leadDate: formatDateString(data?.leadDate, data?.createdAt),
      loginDate: formatDateString(data?.loginDate, data?.dateOfLogin),
      dateOfDisbursment: data?.dateOfDisbursment ? data.dateOfDisbursment.split("T")[0] : "",
      cardDeliveryDate: data?.cardDeliveryDate ? data.cardDeliveryDate.split("T")[0] : "",
      loanSanctionLetterDocuments: data?.loanSanctionLetterDocuments || [],
      chequePhotoDocuments: data?.chequePhotoDocuments || [],
      mortgageDeedDocuments: data?.mortgageDeedDocuments || [],
      insuranceCopyDocuments: data?.insuranceCopyDocuments || [],
    });
  };

  const handleDocumentUpload = async (fieldId, files) => {
    if (!files || files.length === 0) return;
    const allowedFormats = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    const uploadPromises = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (allowedFormats.includes(file.type)) {
        uploadPromises.push(dispatch(uploadImageThunk({ file, type: "LeadBay" })));
      } else {
        dispatch(
          openSnackbar({
            message: "Only JPG, JPEG, PNG, and PDF formats are allowed.",
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
      }
    }
    if (uploadPromises.length > 0) {
      const responses = await Promise.all(uploadPromises);
      const newDocs = responses
        .map((res) => {
          const dataObj = res.payload?.data?.data || res.payload?.data || res.payload;
          const fileName = dataObj?.fileName || dataObj?.name || "";
          const iconUrl = dataObj?.iconUrl || dataObj?.url || "";
          if (iconUrl) {
            return { name: fileName || iconUrl, url: iconUrl };
          }
          return null;
        })
        .filter(Boolean);

      if (newDocs.length > 0) {
        setEditModal((prev) => ({
          ...prev,
          [fieldId]: [...(prev[fieldId] || []), ...newDocs],
        }));
      }
    }
  };

  const handleRemoveDocument = (fieldId, index) => {
    setEditModal((prev) => ({
      ...prev,
      [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index),
    }));
  };

  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState(0);
  const [previewImageList, setPreviewImageList] = useState([]);

  const handleImageFullView = (docList, imageUrl, i) => {
    setPreviewImageList(docList || []);
    setFullScreenImageIndex(i);
    setFullScreenImage(imageUrl);
  };
  const handleCloseFullView = () => {
    setFullScreenImage(null);
  };
  const handleNextImage = () => {
    if (previewImageList.length === 0) return;
    setFullScreenImageIndex((prevIndex) => (prevIndex + 1) % previewImageList.length);
  };
  const handlePreviousImage = () => {
    if (previewImageList.length === 0) return;
    setFullScreenImageIndex(
      (prevIndex) => (prevIndex - 1 + previewImageList.length) % previewImageList.length
    );
  };

  const handleScreenIndex = (type) => {
    switch (type) {
      case "home":
        return 2;
      case "business":
        return 3;
      case "personal":
        return 1;
      case "car":
        return 4;
      case "credit":
        return 0;
      case "education":
        return 5;
      case "loanAgainstProperty":
        return 6;
      case "loanAgainstSecurity":
        return 7;
      case "workingCapital":
        return 8;
      case "machinery":
        return 9;
      case "gold":
        return 10;
      case "credit card":
        return 0;
      default:
        return 0;
    }
  };

  const handleBasicEditModal = (item) => {
    const id = "_id";
    setScreenIndex(handleScreenIndex(item?.type));
    setLeadyBayId(item[id]);
    const temp = {
      type: item?.type ? item?.type : "",
      pinCode: item?.pinCode ? item?.pinCode : "",
      mobileNumber: item.mobileNumber ? `+91${item.mobileNumber}` : "",
      loanAmount: item.loanAmount ? item.loanAmount : "",
      fileNumber: item.fileNumber ? item.fileNumber : "",
      profileType: item.profileType ? item.profileType : "",
      salesManager: item?.salesManager?.sm ? item.salesManager.sm : item?.salesManager || "",
      totalAmountOfEmi: item.totalAmountOfEmi ? item.totalAmountOfEmi : "",
      homeLoanType: item.homeLoanType ? item.homeLoanType : "",
      firstName: item.firstName ? item.firstName : "",
      lastName: item.lastName ? item.lastName : "",
      currentEmi: item.currentEmi ? "yes" : "no",
      email: item.email ? item.email : "",
      gender: item.gender ? item.gender : "",
      dateOfBirth: item.dateOfBirth ? item.dateOfBirth : "",
      dateOfApplication: item.dateOfApplication ? item.dateOfApplication : "",
      leadDate: formatDateString(item?.leadDate, item?.createdAt),
      loginDate: formatDateString(item?.loginDate, item?.dateOfLogin),
      currentStatus: item.currentStatus ? item.currentStatus : "",
      confirmIfYouAreIndianNational: item.confirmIfYouAreIndianNational
        ? item.confirmIfYouAreIndianNational
        : "",
      termsAndConditions: item.termsAndConditions ? item.termsAndConditions : "",
      panCardNumber: item.panCardNumber ? item.panCardNumber : "",
      employmentType: item.employmentType ? item.employmentType : "",
      universityName: item.universityName ? item.universityName : "",
      courseName: item.courseName ? item.courseName : "",
      countryApplyingFor: item.countryApplyingFor ? item.countryApplyingFor : "",
      universityLetterReceived: item.universityLetterReceived ? item.universityLetterReceived : "",
      salaryBankAccountBankName: item.salaryBankAccountBankName
        ? item.salaryBankAccountBankName
        : "",
      netMonthlySalaryCredit: item.netMonthlySalaryCredit ? item.netMonthlySalaryCredit : "",
      lookingForCurrentLoanOutstandingTransfer: item.lookingForCurrentLoanOutstandingTransfer
        ? item.lookingForCurrentLoanOutstandingTransfer
        : "",
      primaryBankAccountBankName: item.primaryBankAccountBankName
        ? item.primaryBankAccountBankName
        : "",
      annualIncomeAsPerItr: item.annualIncomeAsPerItr ? item.annualIncomeAsPerItr : "",
      carLoanType: item.carLoanType ? item.carLoanType : "",
      dynamicFields: item.dynamicFields ? item.dynamicFields : [],
      onRoadPrice: item.onRoadPrice ? item.onRoadPrice : "",
      modelOfCar: item.modelOfCar ? item.modelOfCar : "",
      makerOfCar: item.makerOfCar ? item.makerOfCar : "",
      haveAnycreditCard: item.haveAnycreditCard ? "yes" : "no",
      creditLimit: item.creditLimit ? item.creditLimit : "",
      existingCreditCardBankName: item.existingCreditCardBankName
        ? item.existingCreditCardBankName
        : "",
      images: item?.images || [],
      panCardDocuments: item.panCardDocuments ?? [],
      applicantPhotoDocuments: item.applicantPhotoDocuments ?? [],
      aadharCardDocuments: item.aadharCardDocuments ?? [],
      salarySlipDocuments: item.salarySlipDocuments ?? [],
      sixMonthsbankStatementDocuments: item.sixMonthsbankStatementDocuments ?? [],
      homeElectricityBillDocuments: item.homeElectricityBillDocuments ?? [],
      twelveMonthsbankStatementDocuments: item.twelveMonthsbankStatementDocuments ?? [],
      itrDocuments: item.itrDocuments ?? [],
      officeElectricityBillDocuments: item.officeElectricityBillDocuments ?? [],
      msmeCertificateDocuments: item.msmeCertificateDocuments ?? [],
      gstCertificateDocuments: item.gstCertificateDocuments ?? [],
      otherBusinessProofDocuments: item.otherBusinessProofDocuments ?? [],
      loanSanctionLetterDocuments: item.loanSanctionLetterDocuments ?? [],
      propertyDocuments: item.propertyDocuments ?? [],
      newCarQuotationDocuments: item.newCarQuotationDocuments ?? [],
      oldCarRcDocuments: item.oldCarRcDocuments ?? [],
      creditCardFrontDocuments: item.creditCardFrontDocuments ?? [],
    };
    setUpdateLeadBay({ ...temp });
    setOpenEdit(true);
  };

  const handleCloseEditModal = () => {
    setEditModal({ open: false, ...initialEditModal });
  };

  const handleFetchCibil = async (item) => {
    setFetchCibil({
      open: true,
      loanFormId: item[Constants.MONGOOSE_ID],
      name: `${item.firstName} ${item.lastName}`,
      mobile: item.mobileNumber,
      gender: item.gender,
      pan: item.panCardNumber,
    });
  };

  const handleDelete = async (item) => {
    setDeleteData({ open: true, id: item[Constants.MONGOOSE_ID], loading: false });
  };

  const handleCibilReset = () => {
    setFetchCibil({ open: false, ...initialFetchCibil });
  };

  const userRole = (Session.userRole || "").toLowerCase();
  const isAdmin =
    userRole === (defaultData.ADMIN_ROLE || "admin").toLowerCase() ||
    userRole === (defaultData.SUPER_ADMIN_ROLE || "superadmin").toLowerCase();

  const { columns, rows } = LoanDataTable(
    loanData.loanData,
    handleEditModal,
    handleBasicEditModal,
    handleFetchCibil,
    handleDelete,
    isAdmin
  );
  const columnOptions = useMemo(() => columns, [columns]);
  const sanitizedVisibleColumns = useMemo(
    () =>
      visibleColumns.filter((accessor) =>
        columnOptions.some((column) => column.accessor === accessor)
      ),
    [visibleColumns, columnOptions]
  );
  const filteredVisibleColumns = useMemo(
    () => columns.filter(({ accessor }) => sanitizedVisibleColumns.includes(accessor)),
    [columns, sanitizedVisibleColumns]
  );
  const filterVisibilityOptions = useMemo(() => [...filters, ...search], [filters, search]);

  const handleFilterVisibilityChange = (nextOptions) => {
    const visibleFilterIds = nextOptions
      .filter((option) => option.checked)
      .map((option) => option.id);
    localStorage.setItem(LEAD_BAY_FILTER_VISIBILITY_KEY, JSON.stringify(visibleFilterIds));

    setFilters((currentFilters) =>
      currentFilters.map((filter) => ({
        ...filter,
        checked: visibleFilterIds.includes(filter.id),
      }))
    );
    setSearch((currentSearch) =>
      currentSearch.map((searchItem) => ({
        ...searchItem,
        checked: visibleFilterIds.includes(searchItem.id),
      }))
    );
  };

  const handleOpenManagePanel = (type) => {
    if (type === "columns") {
      setTempVisibleColumns(sanitizedVisibleColumns);
    } else {
      setTempFilterOptions(filterVisibilityOptions);
    }
    setManagePanel({ open: true, type });
  };

  const handleCloseManagePanel = () => {
    setManagePanel((currentPanel) => ({ ...currentPanel, open: false }));
  };

  const handleToggleTempColumn = (accessor) => {
    setTempVisibleColumns((currentColumns) =>
      currentColumns.includes(accessor)
        ? currentColumns.filter((column) => column !== accessor)
        : [...currentColumns, accessor]
    );
  };

  const handleToggleTempFilter = (id) => {
    setTempFilterOptions((currentOptions) =>
      currentOptions.map((option) =>
        option.id === id ? { ...option, checked: !option.checked } : option
      )
    );
  };

  const handleResetManagePanel = () => {
    if (managePanel.type === "columns") {
      setTempVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
      return;
    }

    setTempFilterOptions((currentOptions) =>
      currentOptions.map((option) => ({
        ...option,
        checked: DEFAULT_VISIBLE_FILTER_IDS.includes(option.id),
      }))
    );
  };

  const handleApplyManagePanel = () => {
    if (managePanel.type === "columns") {
      setVisibleColumns(tempVisibleColumns);
    } else {
      handleFilterVisibilityChange(tempFilterOptions);
    }
    handleCloseManagePanel();
  };

  const buildLoanFilterParams = (
    page = 0,
    perPage = tablePagination.perPage,
    tempSearch = search,
    tempFilters = filters
  ) => {
    const getFilterVal = (id) => tempFilters.find((f) => f.id === id)?.selectedValue || "all";
    const getSearchVal = (id) => tempSearch.find((s) => s.id === id)?.selectedValue || "";

    const typeVal = getFilterVal("type");
    const currentStatusVal = getFilterVal("currentStatus");
    const statusVal = getFilterVal("status");
    const financialYearVal = getFilterVal("financialYear");
    const monthVal = getFilterVal("month");
    const bankVal = getFilterVal("bank");
    const codeVal = getFilterVal("code");
    const salesManagerVal = getFilterVal("salesManager");

    return {
      page,
      perPage,
      ...(getSearchVal("firstName") !== "" && { firstName: getSearchVal("firstName") }),
      ...(getSearchVal("mobileNumber") !== "" && {
        mobileNumber: getSearchVal("mobileNumber"),
      }),
      ...(getSearchVal("imd") !== "" && {
        imd: getSearchVal("imd"),
      }),
      ...(typeVal !== "all" && {
        type: mapLoanTypeToBackend(typeVal),
      }),
      ...(currentStatusVal !== "all" && {
        currentStatus: currentStatusVal,
      }),
      ...(statusVal !== "all" && {
        status: statusVal,
      }),
      ...(financialYearVal !== "all" && {
        financialYear: financialYearVal,
      }),
      ...(monthVal !== "all" && {
        month: monthVal,
      }),
      ...(bankVal !== "all" && {
        bank: bankVal,
      }),
      ...(codeVal !== "all" && {
        code: codeVal,
      }),
      ...(salesManagerVal !== "all" && {
        salesManager: salesManagerVal,
      }),
    };
  };

  const handleFilter = async (tempSearch = search, tempFilters = filters) => {
    setTablePagination({ ...tablePagination, page: 0 });
    setNext(0);
    const paramData = buildLoanFilterParams(0, tablePagination.perPage, tempSearch, tempFilters);
    const res = await dispatch(getAllSubmitterLoanFormThunk(paramCreater(paramData)));
    if (res.payload.status !== 200) {
      dispatch(
        openSnackbar({
          message: Constants.MOBILE_USERS_ERROR,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  useEffect(() => {
    (async () => {
      handleFilter();
      const bankParams = {
        bankStatus: true,
      };
      const codeParams = {
        status: true,
      };
      const salesManagerParams = {
        isActive: true,
      };
      const [bankRes, codeRes, salesManagerRes] = await Promise.all([
        dispatch(getAllBanks(paramCreater(bankParams))),
        dispatch(getAllCodes(paramCreater(codeParams))),
        dispatch(getAllSalesManagers(paramCreater(salesManagerParams))),
      ]);
      if (bankRes.payload.status === 200 && codeRes.payload.status === 200) {
        const tempBankList = bankRes.payload.data.data.map((item) => ({
          [Constants.MONGOOSE_ID]: item[Constants.MONGOOSE_ID],
          title: item.bankName,
          image: item.bankLogo || "",
        }));
        const tempCodeList = codeRes.payload.data.data.map((item) => ({
          [Constants.MONGOOSE_ID]: item[Constants.MONGOOSE_ID],
          title: item.code,
        }));
        setFilters((prev) =>
          prev.map((f) => {
            if (f.id === "bank") {
              return {
                ...f,
                list: [{ [Constants.MONGOOSE_ID]: "all", title: "All" }, ...tempBankList],
              };
            }
            if (f.id === "code") {
              return {
                ...f,
                list: [{ [Constants.MONGOOSE_ID]: "all", title: "All" }, ...tempCodeList],
              };
            }
            return f;
          })
        );
        setBankList(tempBankList ?? []);
        setCodeList(tempCodeList);
      }
      if (salesManagerRes.payload.status === 200) {
        const tempSalesManagerList = salesManagerRes.payload.data.data.map((item) => ({
          [Constants.MONGOOSE_ID]: item.sm,
          title: item.sm,
        }));
        setSalesManagerOptions(tempSalesManagerList);
        setFilters((prevFilters) =>
          prevFilters.map((f) => {
            if (f.id === "salesManager") {
              return {
                ...f,
                list: [{ [Constants.MONGOOSE_ID]: "all", title: "All" }, ...tempSalesManagerList],
              };
            }
            return f;
          })
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (
      location.state &&
      (location.state.currentStatus !== undefined ||
        location.state.financialYear !== undefined ||
        location.state.month !== undefined ||
        location.state.type !== undefined)
    ) {
      const nextCurrentStatus = location.state.currentStatus || "all";
      const nextFinancialYear = location.state.financialYear || "all";
      const nextMonth = convertDashboardMonthToCode(location.state.month);
      const nextType = mapLoanTypeToDropdownTitle(location.state.type);

      let updatedFilters;
      setFilters((prevFilters) => {
        const updated = prevFilters.map((f) => {
          if (f.id === "currentStatus") {
            return { ...f, selectedValue: nextCurrentStatus, checked: true };
          }
          if (f.id === "financialYear") {
            return { ...f, selectedValue: nextFinancialYear, checked: true };
          }
          if (f.id === "month") {
            return { ...f, selectedValue: nextMonth, checked: true };
          }
          if (f.id === "type") {
            return { ...f, selectedValue: nextType, checked: true };
          }
          return f;
        });
        updatedFilters = updated;
        return updated;
      });
      if (updatedFilters) {
        handleFilter(search, updatedFilters);
      }
    }
  }, [location.key]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "loginDate") {
      const todayStr = new Date().toISOString().split("T")[0];
      if (value && value > todayStr) {
        setError((prev) => ({ ...prev, loginDate: "Login date cannot be a future date" }));
        return;
      }
      setError((prev) => ({ ...prev, loginDate: "" }));
    }
    if (name === "cardDeliveryDate") {
      setError((prev) => ({ ...prev, cardDeliveryDate: "" }));
    }
    if (name === "currentStatus") {
      setError((prev) => ({ ...prev, currentStatus: "" }));
    }
    setEditModal({ ...editModal, [name]: value });
  };

  const handleUpdateLeadData = async (updateUserOnMail = false) => {
    if (editModal.loginDate) {
      const todayStr = new Date().toISOString().split("T")[0];
      if (editModal.loginDate > todayStr) {
        setError((prev) => ({ ...prev, loginDate: "Login date cannot be a future date" }));
        return;
      }
    }
    if (editModal.currentStatus === "Card Delivered" && !editModal.cardDeliveryDate) {
      setError((prev) => ({ ...prev, cardDeliveryDate: "Please select Card Delivery Date" }));
      return;
    }
    setEditLoading(true);
    const body = {
      ...(editModal.sourceOfLead !== editModal.data.sourceOfLead && {
        sourceOfLead: editModal.sourceOfLead,
      }),
      ...(editModal.imd !== editModal.data.imd && { imd: editModal.imd }),
      ...(editModal.refBy !== editModal.data.refBy && { refBy: editModal.refBy }),
      ...(editModal.fileNumber !== (editModal.data.fileNumber || "") && {
        fileNumber: editModal.fileNumber,
      }),
      ...(editModal.profileType !== (editModal.data.profileType || "") && {
        profileType: editModal.profileType,
      }),
      ...(editModal.salesManager !==
        (editModal.data.salesManager?.sm || editModal.data.salesManager || "") && {
        salesManager: editModal.salesManager,
      }),
      ...(editModal.bank !== editModal.data.bank && { bank: editModal.bank }),
      ...(editModal.code !== editModal.data.code && { code: editModal.code }),
      ...(editModal.currentStatus !== editModal.data.currentStatus && {
        currentStatus: editModal.currentStatus,
      }),
      ...(editModal.leadDate !==
        formatDateString(editModal.data.leadDate, editModal.data.createdAt) && {
        leadDate: editModal.leadDate,
      }),
      ...(editModal.loginDate !==
        formatDateString(editModal.data.loginDate, editModal.data.dateOfLogin) && {
        loginDate: editModal.loginDate,
        dateOfLogin: editModal.loginDate,
      }),
      ...(editModal.remark !== editModal.data.remark && { remark: editModal.remark }),
      ...(editModal.status !== editModal.data.status && { status: editModal.status }),
      ...(editModal.dateOfDisbursment !== editModal.data.dateOfDisbursment && {
        dateOfDisbursment: editModal.dateOfDisbursment,
        updateUserOnMail,
      }),
      ...(editModal.cardDeliveryDate !==
        (editModal.data?.cardDeliveryDate ? editModal.data.cardDeliveryDate.split("T")[0] : "") && {
        cardDeliveryDate: editModal.cardDeliveryDate || null,
      }),
      loanSanctionLetterDocuments: editModal.loanSanctionLetterDocuments || [],
      chequePhotoDocuments: editModal.chequePhotoDocuments || [],
      mortgageDeedDocuments: editModal.mortgageDeedDocuments || [],
      insuranceCopyDocuments: editModal.insuranceCopyDocuments || [],
    };
    const res = await dispatch(updateLeadDataThunk({ id: editModal.id, body }));
    if (res.payload.status === 200) {
      dispatch(
        openSnackbar({
          message: updateUserOnMail
            ? ModalContent.MAIL_ON_UPDATE_SUCCESS_MESSAGE
            : res.payload.data.message,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
      handleCloseEditModal();
      setMailModal(initialMailState);
      await dispatch(updateLoanData(res.payload.data.data));
    } else {
      setError(res.payload.data.message);
    }
    setEditLoading(false);
  };

  const handleDeleteLead = async () => {
    console.log(" deleteData", deleteData);
    setDeleteData({ ...deleteData, loading: true });
    const res = await dispatch(deleteLeadThunk(deleteData.id));
    if (res.payload.status === 200) {
      dispatch(
        openSnackbar({
          message: res.payload.data.message,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
      // remove the deleted lead from the table without refetching
      dispatch(removeLoanData(deleteData.id));
      setDeleteData({ open: false, id: "", loading: false });
    } else {
      dispatch(
        openSnackbar({
          message: res.payload.data.message,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  const handleMailSend = async () => {
    if (editModal.currentStatus !== editModal.data.currentStatus) {
      setMailModal({ ...mailModal, open: true });
    } else handleUpdateLeadData();
  };

  const handleCloseMailModal = () => {
    setMailModal(initialMailState);
    handleUpdateLeadData();
  };

  const handleTablePagination = async () => {
    const data = buildLoanFilterParams(next + 1, tablePagination.perPage);
    const res = await dispatch(getAllSubmitterLoanFormThunk(paramCreater(data)));
    if (res.payload.status === 200) {
      setNext(res.payload.data.data.length > 0 ? next + 1 : next);
    }
  };

  const handleExport = async (format) => {
    const currentDate = new Date();

    const filename = `Finvibes_lead_bay_${currentDate.getFullYear()}-${
      currentDate.getMonth() + 1
    }-${currentDate.getDate()}_${currentDate.getHours()}-${currentDate.getMinutes()}-${currentDate.getSeconds()}.${format}`;

    const res = await dispatch(exportLoanThunk());
    const url = window.URL.createObjectURL(res.payload);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const debouncedHandleSearch = (e, i) => {
    const temp = [...search];
    temp[i].list = [{ [Constants.MONGOOSE_ID]: e.target.value, title: e.target.value }];
    temp[i].selectedValue = e.target.value;
    setSearch(temp);
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(async () => handleFilter(temp), 500);
  };

  const handleFilterChange = (e) => {
    const temp = [...filters];
    const { value, name } = e.target;
    const index = filters.findIndex((filter) => filter.inputLabel === name);
    temp[index].selectedValue = value;
    setFilters(temp);
    handleFilter(search, temp);
  };

  const handleBankFilterChange = (e, selectedBankTitle) => {
    const temp = [...filters];
    const bankFilterIndex = temp.findIndex((filter) => filter.inputLabel === "Bank");
    const selectedBank = bankList.find((bank) => bank.title === selectedBankTitle);
    temp[bankFilterIndex].selectedValue = selectedBank
      ? selectedBank[Constants.MONGOOSE_ID]
      : "all";
    setFilters(temp);
    handleFilter(search, temp);
  };

  const handleResetFilter = () => {
    const resetSearch = search.map((item) => ({
      ...item,
      selectedValue: "",
      list: [],
    }));
    const resetFilters = filters.map((item) => ({
      ...item,
      selectedValue: "all",
    }));
    setSearch(resetSearch);
    setFilters(resetFilters);
    handleFilter(resetSearch, resetFilters);
  };

  return (
    <>
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent={{ xs: "flex-start", md: "flex-end" }}
        flexWrap="wrap"
        gap={1.5}
        sx={{ width: "100%", mb: 2.5 }}
      >
        {leadActions}
        <BasicButton
          title="Filters"
          icon="tune"
          background={Colors.WHITE}
          color={Colors.BLACK}
          action={() => handleOpenManagePanel("filters")}
          ml={pxToRem(0)}
        />
        <BasicButton
          title="Columns"
          icon="view_column"
          background={Colors.WHITE}
          color={Colors.BLACK}
          action={() => handleOpenManagePanel("columns")}
          ml={pxToRem(0)}
        />
        <BasicButton
          title="Export"
          icon={Icons.DOWNLOAD}
          background={Colors.WHITE}
          color={Colors.BLACK}
          action={() => handleExport("xlsx")}
          ml={pxToRem(0)}
        />
        <ResetFilterButton handleReset={handleResetFilter} marginTop={0} />
      </MDBox>
      <MDBox
        display="flex"
        alignItems="start"
        flexWrap="wrap"
        sx={{ flexDirection: "row", mr: 2, mb: 2 }}
        style={{ width: "100%" }}
      >
        {/* Filters */}
        {filters
          ?.filter((filter) => filter.checked)
          .map((val) =>
            val.inputLabel === "Bank" ? (
              <CustomSearch
                key={val.inputLabel}
                isLoading={false}
                options={bankList || []}
                value={
                  bankList.find((bank) => bank[Constants.MONGOOSE_ID] === val.selectedValue)
                    ?.title || ""
                }
                handleFilterChange={handleBankFilterChange}
                placeholder="Search Bank"
                label="Bank"
                width={pxToRem(180)}
                marginRight={pxToRem(10)}
                renderOption={renderBankOption}
              />
            ) : (
              <FilterDropdown
                label={val.inputLabel}
                name={val.inputLabel}
                defaultValue={val?.selectedValue}
                value={val?.selectedValue}
                handleChange={handleFilterChange}
                menu={val.list}
                key={val.inputLabel}
                maxContent={defaultData.MEDIUM_CONTENT_LENGTH}
                minWidth={130}
              />
            )
          )}
        {/* search */}
        {search
          .filter((filter) => filter.checked)
          .map((filter) => (
            <CustomSearch
              isLoading={filter.isLoading}
              options={filter.list}
              value={filter.selectedValue}
              debouncedHandleSearch={(e) => debouncedHandleSearch(e, filter.index)}
              placeholder={`Search ${filter.title}`}
              label={filter.title}
              width={pxToRem(180)}
              marginRight={pxToRem(10)}
              freeSolos
            />
          ))}
      </MDBox>

      <Drawer
        anchor="right"
        open={managePanel.open}
        onClose={handleCloseManagePanel}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: pxToRem(360) },
            borderRadius: 0,
            backgroundColor: "#FFFFFF",
            backgroundImage: "none",
            boxShadow: "0px 16px 40px rgba(16, 24, 40, 0.16)",
          },
        }}
      >
        <MDBox height="100%" display="flex" flexDirection="column">
          <MDBox
            px={2.5}
            py={2}
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <MDBox>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#101828" }}>
                {managePanel.type === "columns" ? "Manage Columns" : "Manage Filters"}
              </Typography>
              <Typography sx={{ fontSize: pxToRem(12), color: "#667085", mt: 0.25 }}>
                {managePanel.type === "columns"
                  ? "Select columns to show in table"
                  : "Select filters to show above table"}
              </Typography>
            </MDBox>
            <IconButton size="small" onClick={handleCloseManagePanel}>
              {Icons.CLOSE}
            </IconButton>
          </MDBox>
          <Divider />
          <MDBox flex={1} px={2.5} py={1.5} sx={{ overflowY: "auto" }}>
            <FormGroup>
              {managePanel.type === "columns"
                ? columnOptions.map((column) => (
                    <MDBox
                      key={column.accessor}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ minHeight: pxToRem(34) }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={tempVisibleColumns.includes(column.accessor)}
                            onChange={() => handleToggleTempColumn(column.accessor)}
                          />
                        }
                        label={column.Header}
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: pxToRem(13),
                            color: "#344054",
                          },
                        }}
                      />
                      <Typography sx={{ color: "#D0D5DD", fontSize: pxToRem(16) }}>::</Typography>
                    </MDBox>
                  ))
                : tempFilterOptions.map((option) => (
                    <MDBox
                      key={option.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ minHeight: pxToRem(34) }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(option.checked)}
                            onChange={() => handleToggleTempFilter(option.id)}
                          />
                        }
                        label={option.title || option.inputLabel}
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: pxToRem(13),
                            color: "#344054",
                          },
                        }}
                      />
                      <Typography sx={{ color: "#D0D5DD", fontSize: pxToRem(16) }}>::</Typography>
                    </MDBox>
                  ))}
            </FormGroup>
          </MDBox>
          <Divider />
          <MDBox px={2.5} py={2} display="flex" gap={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleResetManagePanel}
              sx={{ textTransform: "none", color: "#344054", borderColor: "#D0D5DD" }}
            >
              Reset
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyManagePanel}
              sx={{ textTransform: "none", backgroundColor: "#0047B3", color: "#FFFFFF" }}
            >
              Apply
            </Button>
          </MDBox>
        </MDBox>
      </Drawer>

      <MDBox mt={2}>
        <DataTable
          table={{ columns: filteredVisibleColumns, rows }}
          isSorted={false}
          entriesPerPage={{ defaultValue: defaultData.PER_PAGE }}
          showTotalEntries={false}
          noEndBorder
          loading={loanData.dataLoading}
          handleTablePagination={handleTablePagination}
          currentPage={tablePagination.page}
          handleCurrentPage={(page) => setTablePagination({ ...tablePagination, page })}
        />
      </MDBox>

      {/* Edit Lead Data */}
      <BasicModal
        open={editModal.open}
        handleClose={handleCloseEditModal}
        title={ModalContent.EDIT_LEAD_DATA}
        actionButton={editLoading ? ButtonTitles.UPDATE_LOADING : ButtonTitles.UPDATE}
        handleAction={handleMailSend}
      >
        <ConfigDropdown
          label="Source of Lead*"
          name="sourceOfLead"
          id="sourceOfLead"
          value={editModal.sourceOfLead}
          handleChange={(e, value) => handleChange({ target: { name: "sourceOfLead", value } })}
          menu={leadSources}
          error={Boolean(error?.sourceOfLead)}
          helperText={error?.sourceOfLead}
          minWidth={pxToRem(200)}
        />
        <FTextField
          label="IMD*"
          placeholder="Enter IMD"
          id="imd"
          name="imd"
          type="text"
          error={Boolean(error?.imd)}
          helperText={error?.imd}
          value={editModal.imd}
          handleChange={handleChange}
          marginBottom={2}
        />
        <FTextField
          label="Ref By*"
          placeholder="Enter Ref By"
          id="refBy"
          name="refBy"
          type="text"
          error={Boolean(error?.refBy)}
          helperText={error?.refBy}
          value={editModal.refBy}
          handleChange={handleChange}
          marginBottom={2}
        />
        <FTextField
          label="Loan File"
          placeholder="Enter Loan File Number"
          id="fileNumber"
          name="fileNumber"
          type="text"
          error={Boolean(error?.fileNumber)}
          helperText={error?.fileNumber}
          value={editModal.fileNumber}
          handleChange={handleChange}
          marginBottom={2}
        />
        <FDropdown2
          label="Profile Type"
          id="profileType"
          name="profileType"
          value={editModal.profileType}
          defaultValue=""
          options={profileTypeOptions}
          error={Boolean(error?.profileType)}
          helperText={error?.profileType}
          handleChange={handleChange}
          marginBottom={pxToRem(10)}
        />
        <FDropdown2
          label="Sales Manager"
          id="salesManager"
          name="salesManager"
          value={editModal.salesManager}
          defaultValue=""
          options={salesManagerOptions}
          error={Boolean(error?.salesManager)}
          helperText={error?.salesManager}
          handleChange={handleChange}
          marginBottom={pxToRem(10)}
        />
        <CustomSearch
          isLoading={false}
          options={bankList || []}
          value={
            bankList.find((bank) => bank[Constants.MONGOOSE_ID] === editModal.bank)?.title || ""
          }
          handleFilterChange={(e, selectedBankTitle) => {
            const selectedBank = bankList.find((bank) => bank.title === selectedBankTitle);
            handleChange({
              target: {
                name: "bank",
                value: selectedBank ? selectedBank[Constants.MONGOOSE_ID] : "",
              },
            });
          }}
          placeholder="Search Bank"
          label="Bank*"
          width="100%"
          marginRight={0}
          error={Boolean(error?.bank)}
          helperText={error?.bank}
          showSearchIcon={false}
          renderOption={renderBankOption}
        />
        <FDropdown2
          label="Code*"
          id="code"
          name="code"
          value={editModal.code}
          defaultValue=""
          options={codeList || []}
          error={Boolean(error?.code)}
          helperText={error?.code}
          handleChange={handleChange}
          marginBottom={pxToRem(10)}
        />

        <FTextField
          label="Lead Date"
          placeholder="Select Lead Date"
          id="leadDate"
          name="leadDate"
          type="date"
          error={Boolean(error?.leadDate)}
          helperText={error?.leadDate}
          value={editModal.leadDate}
          handleChange={handleChange}
          marginBottom={2}
        />
        <FTextField
          label="Login Date"
          placeholder="Select Login Date"
          id="loginDate"
          name="loginDate"
          type="date"
          error={Boolean(error?.loginDate)}
          helperText={error?.loginDate}
          value={editModal.loginDate}
          handleChange={handleChange}
          inputProps={{ max: new Date().toISOString().split("T")[0] }}
          marginBottom={2}
        />

        <ConfigDropdown
          label="Current Status*"
          name="currentStatus"
          id="currentStatus"
          value={editModal.currentStatus}
          handleChange={(e, value) => handleChange({ target: { name: "currentStatus", value } })}
          menu={editStatusMenu}
          error={Boolean(error?.currentStatus)}
          helperText={error?.currentStatus}
          minWidth={pxToRem(200)}
        />
        {editModal.currentStatus === "Disbursement" && (
          <FTextField
            label="Date Of Disbursement*"
            placeholder="Enter Date Of Disbursement"
            id="dateOfDisbursment"
            name="dateOfDisbursment"
            type="date"
            error={Boolean(error?.dateOfDisbursment)}
            helperText={error?.dateOfDisbursment}
            value={editModal.dateOfDisbursment}
            handleChange={handleChange}
            marginBottom={2}
          />
        )}
        {editModal.currentStatus === "Card Delivered" && (
          <FTextField
            label="Card Delivery Date*"
            placeholder="Select Card Delivery Date"
            id="cardDeliveryDate"
            name="cardDeliveryDate"
            type="date"
            error={Boolean(error?.cardDeliveryDate)}
            helperText={error?.cardDeliveryDate}
            value={editModal.cardDeliveryDate}
            handleChange={handleChange}
            marginBottom={2}
          />
        )}
        <FTextField
          label="Remark*"
          placeholder="Enter Remark"
          id="remark"
          name="remark"
          type="textarea"
          rows={3}
          error={Boolean(error?.remark)}
          helperText={error?.remark}
          value={editModal.remark}
          handleChange={handleChange}
          marginBottom={2}
        />

        {[
          { id: "loanSanctionLetterDocuments", label: "Sanction Letter" },
          { id: "chequePhotoDocuments", label: "Cheque Photo" },
          { id: "mortgageDeedDocuments", label: "Mortgage Deed" },
          { id: "insuranceCopyDocuments", label: "Insurance Copy" },
        ].map((item) => (
          <MDBox key={item.id} sx={{ mt: pxToRem(8), mb: pxToRem(10), width: "100%" }}>
            <MDTypography
              variant="caption"
              sx={{ fontSize: pxToRem(14), fontWeight: 500, color: "#344054", display: "block" }}
            >
              {item.label}
            </MDTypography>
            <MDBox
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={1.5}
              flexWrap="wrap"
              sx={{ mt: pxToRem(6) }}
            >
              {(editModal[item.id] || []).map((img, i) => (
                <MDBox
                  display="flex"
                  borderRadius="8px"
                  position="relative"
                  key={img.url || `${item.id}-${i}`}
                  sx={{
                    "&:hover .overlay": { display: "flex", borderRadius: "8px" },
                  }}
                >
                  <img
                    src={img?.name?.includes(".pdf") || img?.url?.includes(".pdf") ? Pdf : img?.url}
                    alt="Preview"
                    width={70}
                    height={70}
                    style={{ borderRadius: "8px", objectFit: "cover" }}
                  />
                  <Box
                    display="none"
                    position="absolute"
                    top={0}
                    right={35}
                    bottom={0}
                    left={0}
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(0, 0, 0, 0.5)"
                    className="overlay"
                  >
                    <Icon
                      sx={{
                        color: "white",
                        width: 24,
                        height: 24,
                        cursor: "pointer",
                      }}
                      onClick={() => handleImageFullView(editModal[item.id], img?.url, i)}
                    >
                      {Icons.VIEW2}
                    </Icon>
                  </Box>
                  <Box
                    display="none"
                    position="absolute"
                    top={0}
                    right={0}
                    bottom={0}
                    left={35}
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(0, 0, 0, 0.5)"
                    className="overlay"
                  >
                    <Icon
                      sx={{
                        color: "white",
                        width: 24,
                        height: 24,
                        cursor: "pointer",
                      }}
                      onClick={() => handleRemoveDocument(item.id, i)}
                    >
                      {Icons.CROSS2}
                    </Icon>
                  </Box>
                </MDBox>
              ))}
              <MDBox
                component="label"
                width={70}
                height={70}
                borderRadius="8px"
                sx={{
                  border: "2px dashed #D0D5DD",
                  borderRadius: "8px",
                  cursor: "pointer",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                {Icons.ADD}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={(e) => handleDocumentUpload(item.id, e.target.files)}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        ))}

        <ConfigDropdown
          label="Status*"
          name="status"
          id="status"
          value={editModal.status}
          handleChange={(e, value) => handleChange({ target: { name: "status", value } })}
          menu={["open", "close", "hold", "completed"]}
          error={Boolean(error?.status)}
          helperText={error?.status}
          minWidth={pxToRem(200)}
        />
      </BasicModal>

      {openEdit && (
        <UpdateLeadBay
          selectedCardId={leadyBayId}
          leadBayData={updateLeadbay}
          openEdit={openEdit}
          setOpenEdit={setOpenEdit}
          screenIndex={screenIndex}
          handleFilter={handleFilter}
        />
      )}

      {fetchCibil.open && (
        <ViewCibil
          open={fetchCibil.open}
          loanFormId={fetchCibil.loanFormId}
          name={fetchCibil.name}
          mobile={fetchCibil.mobile}
          gender={fetchCibil.gender}
          pan={fetchCibil.pan}
          handleClose={handleCibilReset}
        />
      )}

      {/* Mail on delete modal */}
      {deleteData.open && (
        <ConfirmationModal
          open={deleteData.open}
          handleClose={() => setDeleteData({ open: false, id: "" })}
          title={ModalContent.LOAN_FORM_DELETE_TITLE}
          message={ModalContent.LOAN_FORM_DELETE_MESSAGE}
          handleAction={handleDeleteLead}
        />
      )}

      <ConfirmationModal
        open={mailModal.open}
        handleClose={handleCloseMailModal}
        title={ModalContent.MAIL_ON_UPDATE_TITLE}
        message={ModalContent.MAIL_ON_UPDATE_MESSAGE}
        handleAction={() => handleUpdateLeadData(true)}
      />

      {fullScreenImage && (
        <FullScreenImageComponent
          fullScreenImage={fullScreenImage}
          handleCloseFullView={handleCloseFullView}
          handlePreviousImage={handlePreviousImage}
          handleNextImage={handleNextImage}
          image={previewImageList}
          src={
            typeof previewImageList[fullScreenImageIndex] === "object"
              ? previewImageList[fullScreenImageIndex]?.url
              : previewImageList[fullScreenImageIndex]
          }
        />
      )}
    </>
  );
}

export default feedbacks;
